// Sends the FIG login token to the azuresql data API — and nowhere else.
//
// Phase A of docs/superpowers/plans/2026-11-03-data-api-auth.md (§3c). Until now only
// GetCallApi (the custom Axios instance in AxiosConfig.jsx) sent `Authorization: Bearer`;
// every plain axios/fetch call to the scaffold went out anonymously. The server side is in
// `monitor` during phase A, so attaching the header changes no response — it only lets the
// access log attribute each call to a user before writes and reads are closed.
//
// Scope rules (all must hold):
//   1. the request's origin is the data API's origin, derived from Config.DATA_API — so a
//      figdev build pointed elsewhere follows automatically and nothing is hardcoded twice;
//   2. the path is one of the FIG-login route groups below. `partnerportal` and the public
//      course participant routes are deliberately excluded: both read `Authorization: Bearer`
//      as THEIR OWN session token (PublicAccount / participant session), so a FIG token there
//      would be misread as a bad session;
//   3. a token exists (logged-out public pages send exactly what they sent before);
//   4. the request carries no Authorization header already (never override an explicit one).
import Config from "./Config";

const resolveOrigin = (url) => {
  try {
    return new URL(url).origin;
  } catch (_) {
    return null;
  }
};

export const DATA_API_ORIGIN = resolveOrigin(Config.DATA_API);

// First path segment after /api/ that takes the FIG login token.
const TOKEN_SEGMENTS = new Set(
  [
    "the",
    "dev",
    String(Config.DATA_API_SLUG || "").toLowerCase(),
    "support",
    "intake",
    "evaluations",
    "portfolio",
    "coursesearch",
    "upload",
  ].filter(Boolean)
);

// Staff-only sub-routes inside otherwise-public route groups.
const TOKEN_SUBROUTES = [
  /^\/api\/publiccourse\/[^/]+\/crm(\/|$)/i, // registrant -> CRM admin tools
  /^\/api\/checkin\/send-cert(\/|$)/i, // staff approve + send certificate
];

const baseForRelative = () =>
  typeof window !== "undefined" && window.location ? window.location.href : "http://localhost/";

/**
 * True when a request to `url` (optionally resolved against `baseURL`) should carry the
 * FIG login token. Relative URLs resolve against baseURL, else the page — so they can only
 * match when the page itself is served from the data API origin, which it never is.
 */
export const isDataApiAuthUrl = (url, baseURL) => {
  if (!DATA_API_ORIGIN || url == null) return false;
  let parsed;
  try {
    const raw = typeof url === "string" ? url : String(url.url || url.href || url);
    const base = baseURL ? new URL(baseURL, baseForRelative()).href : baseForRelative();
    parsed = new URL(raw, base);
  } catch (_) {
    return false;
  }
  if (parsed.origin !== DATA_API_ORIGIN) return false;
  const path = parsed.pathname;
  if (TOKEN_SUBROUTES.some((re) => re.test(path))) return true;
  const m = /^\/api\/([^/]+)(\/|$)/i.exec(path);
  return !!m && TOKEN_SEGMENTS.has(m[1].toLowerCase());
};

export const defaultTokenGetter = () => {
  try {
    // Required lazily: the store pulls in every reducer, and this module is imported by
    // plain helpers that must stay cheap to load in tests.
    const store = require("../store/index").default;
    return store?.getState?.()?.login?.LoginDetails?.accessToken || null;
  } catch (_) {
    return null;
  }
};

const hasAuthHeader = (headers) => {
  if (!headers) return false;
  // axios AxiosHeaders and fetch Headers are both case-insensitive via has()
  if (typeof headers.has === "function") return !!headers.has("Authorization");
  if (Array.isArray(headers)) return headers.some(([k]) => String(k).toLowerCase() === "authorization");
  return Object.keys(headers).some((k) => k.toLowerCase() === "authorization" && headers[k] != null && headers[k] !== "");
};

/** axios request interceptor body — exported for tests. */
export const makeDataApiRequestInterceptor = (getToken = defaultTokenGetter) => (config) => {
  if (!config || !isDataApiAuthUrl(config.url, config.baseURL)) return config;
  if (hasAuthHeader(config.headers)) return config;
  const token = getToken();
  if (!token) return config;
  if (config.headers && typeof config.headers.set === "function") {
    config.headers.set("Authorization", `Bearer ${token}`);
  } else {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` };
  }
  return config;
};

const installed = new WeakSet();

/** Registers the interceptor on an axios instance once (idempotent). */
export const installDataApiAuth = (instance, getToken = defaultTokenGetter) => {
  if (!instance || !instance.interceptors || installed.has(instance)) return;
  installed.add(instance);
  instance.interceptors.request.use(makeDataApiRequestInterceptor(getToken));
};

/**
 * fetch() with the same rule as the axios interceptor. Same signature and return value as
 * window.fetch — call sites keep their own response/error handling unchanged.
 */
export const createDataApiFetch = (getToken = defaultTokenGetter, fetchImpl) => (input, init) => {
  const doFetch = fetchImpl || ((...args) => window.fetch(...args));
  const url = typeof input === "string" ? input : input && (input.url || input.href);
  if (!isDataApiAuthUrl(url)) return doFetch(input, init);

  const reqHeaders = input && typeof input === "object" && input.headers;
  if (hasAuthHeader(init && init.headers) || hasAuthHeader(reqHeaders)) return doFetch(input, init);

  const token = getToken();
  if (!token) return doFetch(input, init);

  const given = init && init.headers;
  let headers;
  if (given && typeof Headers !== "undefined" && given instanceof Headers) {
    headers = new Headers(given);
    headers.set("Authorization", `Bearer ${token}`);
  } else if (Array.isArray(given)) {
    headers = [...given, ["Authorization", `Bearer ${token}`]];
  } else {
    headers = { ...(given || {}), Authorization: `Bearer ${token}` };
  }
  return doFetch(input, { ...(init || {}), headers });
};

export const dataApiFetch = createDataApiFetch();
